import { adminFetch } from "./_client";
import type { DriverVerificationCriteria, DriverVerificationQueueResponse, DriverVerificationDetail, UnboundVerificationStartsResponse, BookingMvrAttempt, BookingMvrDetail } from "./types";

export interface InternationalDriverCheck {
  id: number;
  trip_id: number;
  driver_id: number;
  didit_session_id: string;
  issuing_country: string;
  status: "pending" | "rejected" | "approved" | string;
  notes: string | null;
  reviewed_at: string | null;
  created_at: string;
  evidence_origin: "booking_context" | "account_level_reuse" | string;
  booking_reference: string;
  booking_status: string | null;
  payment_status: number | null;
  verification_hold: boolean | null;
  pickup_at: string | null;
  dropoff_at: string | null;
  renter: { id: number; name: string; email: string | null } | null;
  host: { id: number; name: string; email: string | null } | null;
  selected_driver: { id: number; account_id: number; name: string; email: string | null } | null;
  car: { id: number; title: string; city: string | null; state: string | null; country_code: string | null } | null;
  driver_licence: {
    extracted_name: string | null;
    issuing_country: string | null;
    issuing_state: string | null;
    date_of_birth: string | null;
    expiry: string | null;
    document_number_masked: string | null;
    review_decision: string | null;
    license_verification_status: number | null;
    evidence_purge_after: string | null;
    front_object_present: boolean;
    back_object_present: boolean;
  } | null;
  current_didit_session: string | null;
  current_issuing_country: string | null;
  driver_name: string;
  legal_driving_source_reference: string | null;
  insurance_policy_source_reference: string | null;
  insurance_policy_snapshot: unknown | null;
  insurance_policy_snapshot_suggestion_id: number | null;
}

export interface InternationalDriverCheckChecklist {
  licenceAuthenticityConfirmed: boolean;
  usDrivingEligibilityConfirmed: boolean;
  insuranceEligibilityConfirmed: boolean;
}

export interface InternationalDriverCheckDecisionResponse {
  success: boolean;
  bookingRelease: { ok: boolean; reason?: string };
}

export interface ReconcileInternationalDriverCheckResponse {
  success: boolean;
  booking_reference: string;
  check_status: "pending" | "approved" | "rejected" | "superseded";
  message: string;
}

export function fetchInternationalDriverChecks(token: string, page = 1, view: "active" | "history" = "active") {
  return adminFetch<{ checks: InternationalDriverCheck[]; pendingTotal: number; total: number; page: number; limit: number }>(
    `/admin/driver-verification/international-checks?page=${page}&view=${view}`,
    token,
  );
}

export function reconcileInternationalDriverCheck(token: string, bookingReference: string) {
  return adminFetch<ReconcileInternationalDriverCheckResponse>(
    "/admin/driver-verification/international-checks/reconcile",
    token,
    {
      method: "POST",
      body: JSON.stringify({ booking_reference: bookingReference }),
    },
  );
}

export function decideInternationalDriverCheck(
  token: string,
  id: number,
  body: {
    decision: "approved" | "rejected";
    notes?: string;
    checklist?: InternationalDriverCheckChecklist;
    legal_driving_source_reference?: string;
    insurance_policy_source_reference?: string;
  },
) {
  return adminFetch<InternationalDriverCheckDecisionResponse>(
    `/admin/driver-verification/international-checks/${id}/decision`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function fetchDriverVerificationCriteria(token: string) {
  return adminFetch<{ criteria: DriverVerificationCriteria }>("/admin/driver-verification/criteria", token);
}


export function updateDriverVerificationCriteria(token: string, criteria: DriverVerificationCriteria) {
  return adminFetch<{ success: boolean; criteria: DriverVerificationCriteria }>("/admin/driver-verification/criteria", token, {
    method: "PUT",
    body: JSON.stringify({ criteria }),
  });
}


export function fetchDriverVerificationQueue(token: string, params: { page?: number; reason?: string; includeResolved?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set("page", String(params.page));
  if (params.reason) qs.set("reason", params.reason);
  if (params.includeResolved) qs.set("include_resolved", "1");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return adminFetch<DriverVerificationQueueResponse>(`/admin/driver-verification/queue${suffix}`, token);
}

export function fetchBookingMvrQueue(token: string, page = 1) {
  return adminFetch<{ attempts: BookingMvrAttempt[]; total: number; page: number; limit: number }>(
    `/admin/driver-verification/booking-mvr?page=${page}`, token,
  );
}

export function fetchBookingMvrDetail(token: string, attemptId: number) {
  return adminFetch<BookingMvrDetail>(`/admin/driver-verification/booking-mvr/${attemptId}`, token);
}

export function resolveBookingMvr(token: string, attemptId: number, tripId: number, decision: "approved" | "rejected", notes: string) {
  return adminFetch<{ success: boolean; status: string; released: boolean; releaseReason?: string; replay: boolean }>(
    `/admin/driver-verification/booking-mvr/${attemptId}/resolve`, token,
    { method: "POST", body: JSON.stringify({ trip_id: tripId, decision, notes }) },
  );
}

export function fetchUnboundVerificationStarts(token: string, page = 1) {
  return adminFetch<UnboundVerificationStartsResponse>(
    `/admin/driver-verification/unbound-starts?page=${page}`, token,
  );
}


export function fetchDriverVerificationDetail(token: string, driverId: number) {
  return adminFetch<DriverVerificationDetail>(`/admin/driver-verification/${driverId}`, token);
}


export function approveDriverVerification(token: string, driverId: number, notes?: string) {
  return adminFetch<{ success: boolean }>(`/admin/driver-verification/${driverId}/approve`, token, {
    method: "POST",
    body: JSON.stringify({ notes: notes ?? null }),
  });
}


export function rejectDriverVerification(token: string, driverId: number, notes?: string) {
  return adminFetch<{ success: boolean }>(`/admin/driver-verification/${driverId}/reject`, token, {
    method: "POST",
    body: JSON.stringify({ notes: notes ?? null }),
  });
}

export function resetDiditVerificationAttempts(
  token: string,
  driverId: number,
  reason: string,
  assignmentId?: number | null,
  bookingTripId?: number | null,
) {
  return adminFetch<{
    success: boolean;
    reset_at: string | null;
    previous_attempts: number | null;
  }>(`/admin/driver-verification/${driverId}/reset-didit-attempts`, token, {
    method: "POST",
    body: JSON.stringify({
      reason,
      assignment_id: assignmentId ?? null,
      booking_trip_id: bookingTripId ?? null,
    }),
  });
}


// Task #8211 — archived licence evidence for host-invited drivers. Served
// through the existing step-up-protected, audited, short-lived signed-URL
// path; the admin client never touches transient Didit URLs.
export function fetchDriverLicenceImage(token: string, driverId: number, side: "front" | "back") {
  return adminFetch<{ signed_url: string; expires_in_sec: number; side: "front" | "back" }>(
    `/admin/driver-verification/${driverId}/licence-image/${side}`,
    token,
  );
}


// Task #866: indefinite retention hold — pin a driver's evidence past the
// 30-day purge window with a required free-text reason; release restores
// the standard 30-day window from "now".
export function setDriverEvidenceRetentionHold(token: string, driverId: number, reason: string) {
  return adminFetch<{ success: boolean; reason: string; set_at: string; set_by: string }>(
    `/admin/driver-verification/${driverId}/retention-hold`,
    token,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}


export function releaseDriverEvidenceRetentionHold(token: string, driverId: number) {
  return adminFetch<{ success: boolean; evidence_purge_after: string }>(
    `/admin/driver-verification/${driverId}/retention-hold`,
    token,
    { method: "DELETE" },
  );
}
